import {
  FORM_ALIGNMENT,
  FORM_FIELD_KEYS,
  FORM_LAYOUT_OPTIONS,
  PREVIEW_TYPE,
  VIDEO_PLACEMENT,
} from './constants';

export type Enum<T> = T[keyof T];

type Picture = {
  active: boolean;
  baseLink: string;
  defaultPicture: boolean;
  link?: string;
  resourceKey: string;
  sizes: {
    height: number;
    link: string;
    linkWithPlayButton?: string;
    width: number;
  }[];
  type: 'caution' | 'custom' | 'default';
  uri: string;
};

export interface UserProps {
  previewType?: Enum<typeof PREVIEW_TYPE>;
  /**
   * This function will be called in the following scenarios
   * 1. User does a fresh successfull registration
   * 2. User does a login using already registered
   * 3. User was already registered and logged in (ie.. cookie was already set)
   */
  onRegistrationSuccess?: (cookie: string) => void;
}

export interface ConnectedField {
  id?: number;
  leadCaptureFormFieldId?: number;
  emailServiceProviderFieldId?: string;
  emailServiceProviderType: number;
}

export interface FieldOption {
  optionLabel: string;
  optionPosition: number;
  cid: number;
}

export interface Field {
  id?: number;
  cid: number;
  name: string;
  position: number;
  isRequired: boolean;
  isLocked: boolean;
  type: string;
  isStatic: boolean;
  metadata?: {
    validatorType?: 'email'; // TODO, This value will be used once metadata field is expanded in backend
    fieldKey?: Enum<typeof FORM_FIELD_KEYS>; // TODO, This value will be used once metadata field is expanded in backend. This will be used to uniquely identify which field is which. This is needed because while sending registrant data to backend, firstName, lastName and email should be send seperately.
    options?: FieldOption[];
    description?: string;
    color?: string;
  };
  isHidden: boolean;
  connectedFields: ConnectedField[];
}

interface Form {
  privacyPolicyUrl: string;
  hasActiveCrmExportConnection: boolean;
  customFields: Field[];
  hiddenFields: Field[];
  emailLists: EmailCaptureFormList[];
}

export interface LogoType {
  uri: string;
  url: string;
  customLink: string;
  isLinkActive: boolean;
  isActive: boolean;
  pictures?: Picture;
}

export interface BackgroundType {
  style: string;
  type: string;
  uri: string;
  url: string;
  isActive: boolean;
  hexCode: string;
  opacity: number;
  pictures?: Picture;
  useThumbnail: boolean;
}
interface ColorProperty {
  color: string;
}
export interface NonEditableTextStyle {
  eventDate: ColorProperty;
  alreadyRegistered: ColorProperty;
  termsAndConditions: ColorProperty;
  privacyPolicy: ColorProperty;
  dontHaveAccount: ColorProperty;
  addToCalendar: ColorProperty;
  logIn: ColorProperty;
  skipToVideo: ColorProperty;
}

export interface Appearance {
  layout: Enum<typeof FORM_LAYOUT_OPTIONS>;
  alignment: Enum<typeof FORM_ALIGNMENT>;
  isSkippable: boolean;
  placement: Enum<typeof VIDEO_PLACEMENT>;
  placementTimecode: number;
  logo: LogoType;
  background: BackgroundType;
  isEventDateSetToCalendar: boolean;
  isEventDateVisible: boolean;
  nonEditableTextStyle: NonEditableTextStyle;
}

interface Preview extends PreviewHTML, PreviewButton {}

export interface PreviewHTML {
  joinPageTitle: string;
  loginPageTitle: string;
  confirmationPageTitle: string;
  confirmationPageDescription: string;
}

interface PreviewButton {
  primaryButtonStyle: ButtonRtfValues;
  secondaryButtonStyle: ButtonRtfValues;
}

export interface LeadCapture extends Preview, Appearance, Form, CalendarLinks {
  uri: string;
  uuid: string;
  privacyPolicyVersion?: number;
  enable: boolean;
}

export interface CalendarLinks {
  calendarLinks?: {
    google?: string;
    outlook?: string;
    ical?: string;
  };
}

export interface Logo {
  height: number;
  width: number;
  link: string;
  uri: string;
  isDeleting?: boolean;
}

export interface EmailCaptureFormList {
  id?: number;
  formId?: number;
  providerId: string;
  providerType?: number;
  listId: string;
}

export interface PreviewDetails {
  height?: number;
  width?: number;
  boundingBox?: DOMRect;
}
export interface ButtonRtfValues {
  nextButtonText?: string;
  registerButtonText?: string;
  loginButtonText?: string;
  backButtonText?: string;
  role?: string;
  font?: string;
  style?: string[];
  type?: string;
  textColor?: string;
  buttonColor?: string;
}

export const EVENT_STATUS = { STARTED: 'started', ENDED: 'ended', EMPTY: null } as const;

export type EVENT_STATUS_TYPE = Enum<typeof EVENT_STATUS>;
